import {
    AGENDA_ITEM_PRINTOUT_VARIABLES,
    FOLIO_ITEM_PRINTOUT_VARIABLES,
    GUEST_PRINTOUT_VARIABLES,
    RESERVATION_PRINTOUT_VARIABLES,
    RESOURCE_PRINTOUT_VARIABLES,
    normalizePrintoutDateFormat,
} from "../../variables/printoutVariables";
import type {PrintoutDateFormat} from "../../variables/printoutVariables";
import type {PrintoutBlockTextStyle} from "../shared/blockAttributes";
import {
    parseEnumAttribute,
    parseTextAttribute,
    parseTextStyleAttribute,
} from "../shared/blockAttributes";

export type DataListSource = "folioItems" | "reservationMealPeriods" | "guestDietaryRequirements" | "agendaItems";
export type DataListAlign = "left" | "center" | "right";

export const DATA_LIST_AGENDA_SECTOR_TYPES = [
    "WEEKLY_PLANNER",
    "SPA",
    "RESTAURANT",
    "OTHER",
] as const;
export type DataListAgendaSectorType = typeof DATA_LIST_AGENDA_SECTOR_TYPES[number];

export type DataListTextStyle = PrintoutBlockTextStyle;

export const DATA_LIST_SOURCES: DataListSource[] = ["folioItems", "reservationMealPeriods", "guestDietaryRequirements", "agendaItems"];
export const DATA_LIST_ALIGNS: DataListAlign[] = ["left", "center", "right"];

export interface DataListColumn {
    id: string;
    variableKey: string;
    label: string;
    align: DataListAlign;
    dateFormat?: PrintoutDateFormat | null;
    headerFormat: DataListTextStyle;
    cellFormat: DataListTextStyle;
}

export interface PrintoutDataListAttrs {
    source: DataListSource;
    columns: DataListColumn[];
    emptyText: string;
    title: string;
    hideWhenEmpty: boolean;
    sectorTypes: DataListAgendaSectorType[];
}

// Preview-only resolved data; never serialized.
export interface DataListResolved {
    items: Record<string, string>[];
}

interface SourceDefinition {
    label: string;
    description: string;
    emptyText: string;
    variables: readonly {key: string; label: string}[];
    defaultColumnKeys: string[];
    columnLabels?: Record<string, string>;
    defaultTitle?: string;
}

export const DATA_LIST_SOURCE_DEFINITIONS: Record<DataListSource, SourceDefinition> = {
    folioItems: {
        defaultColumnKeys: ["folioItemDescription", "folioItemQuantity", "folioItemTotal"],
        description: "Line items from the current folio.",
        emptyText: "No folio items found.",
        label: "Folio items",
        variables: FOLIO_ITEM_PRINTOUT_VARIABLES,
    },
    reservationMealPeriods: {
        defaultColumnKeys: ["resStartDate", "resMealPeriod", "resStartTime", "resRestaurantName", "resGuestBreakdown"],
        columnLabels: {
            resMealPeriod: "Meal",
            resStartDate: "Date",
            resStartTime: "Time",
            resRestaurantName: "Restaurant",
            resTableNames: "Table",
            resGuestBreakdown: "Guests",
        },
        defaultTitle: "Your meals",
        description: "Saved breakfast, lunch and dinner bookings in this reservation group and sector. Cancelled, no-show and provisional bookings are excluded. Meals are grouped by date.",
        emptyText: "No meal reservations found.",
        label: "Meal plan reservations",
        variables: [...RESERVATION_PRINTOUT_VARIABLES, ...RESOURCE_PRINTOUT_VARIABLES],
    },
    guestDietaryRequirements: {
        defaultColumnKeys: ["guestFullName", "guestAllergies", "guestDietaryPreferences"],
        columnLabels: {
            guestFullName: "Guest",
            guestAllergies: "Food allergies",
            guestDietaryPreferences: "Dietary preferences",
        },
        defaultTitle: "Recorded dietary requirements",
        description: "Food allergies and dietary preferences for each guest on this reservation. Guests without recorded requirements, hidden entries and medical information are excluded. Blank values do not mean no allergies.",
        emptyText: "No dietary requirements recorded for this booking.",
        label: "Guest dietary requirements",
        variables: GUEST_PRINTOUT_VARIABLES.filter(variable => (
            ["guestFullName", "guestAllergies", "guestDietaryPreferences"].includes(variable.key)
        )),
    },
    agendaItems: {
        defaultColumnKeys: [
            "agendaItemStartDate",
            "agendaItemTimeRange",
            "agendaItemName",
            "agendaItemLocation",
            "agendaItemType",
        ],
        columnLabels: {
            agendaItemLocation: "Where",
            agendaItemName: "Reservation",
            agendaItemSector: "Type",
            agendaItemType: "Type",
            agendaItemStartDate: "Date",
            agendaItemStartTime: "Time",
            agendaItemTimeRange: "Time",
        },
        description: "The guest's booked activities during their stay — spa, restaurant, weekly planner and other bookings. The stay itself (accommodation) is excluded.",
        emptyText: "This guest has no agenda.",
        label: "Agenda items",
        variables: AGENDA_ITEM_PRINTOUT_VARIABLES,
    },
};

export const getDataListSourceDefinition = (source: DataListSource) => (
    DATA_LIST_SOURCE_DEFINITIONS[source] ?? DATA_LIST_SOURCE_DEFINITIONS.folioItems
);

export const getDataListVariableLabel = (source: DataListSource, variableKey: string) => (
    getDataListSourceDefinition(source).variables.find(variable => variable.key === variableKey)?.label
    ?? variableKey
);

export const getDataListColumnLabel = (source: DataListSource, variableKey: string) => (
    getDataListSourceDefinition(source).columnLabels?.[variableKey]
    ?? getDataListVariableLabel(source, variableKey)
);

const RIGHT_ALIGN_KEYS = new Set([
    "folioItemPrice",
    "folioItemTotal",
    "folioItemTotalBeforeTax",
    "folioItemDiscount",
    "folioItemTaxRate",
]);
const CENTER_ALIGN_KEYS = new Set(["folioItemQuantity", "resGuestCount"]);

export const getDataListDefaultAlign = (variableKey: string): DataListAlign => {
    if (RIGHT_ALIGN_KEYS.has(variableKey)) return "right";
    if (CENTER_ALIGN_KEYS.has(variableKey)) return "center";

    return "left";
};

export const createDataListId = () => Math.random().toString(36).slice(2, 10);

const normalizeColumn = (
    source: DataListSource,
    column: Partial<DataListColumn> | null | undefined,
): DataListColumn => {
    const variableKey = parseTextAttribute(column?.variableKey, getDataListSourceDefinition(source).defaultColumnKeys[0]);

    return {
        align: parseEnumAttribute(column?.align, DATA_LIST_ALIGNS, getDataListDefaultAlign(variableKey)),
        cellFormat: parseTextStyleAttribute(column?.cellFormat),
        dateFormat: normalizePrintoutDateFormat(column?.dateFormat),
        headerFormat: parseTextStyleAttribute(column?.headerFormat),
        id: parseTextAttribute(column?.id, createDataListId()),
        label: parseTextAttribute(column?.label, getDataListColumnLabel(source, variableKey)),
        variableKey,
    };
};

export const createDataListColumns = (source: DataListSource): DataListColumn[] => (
    getDataListSourceDefinition(source).defaultColumnKeys.map(variableKey => ({
        align: getDataListDefaultAlign(variableKey),
        cellFormat: {bold: false, italic: false},
        headerFormat: {bold: false, italic: false},
        id: createDataListId(),
        label: getDataListColumnLabel(source, variableKey),
        variableKey,
    }))
);

const normalizeSectorTypes = (value: unknown): DataListAgendaSectorType[] => (
    Array.isArray(value)
        ? value.filter((type): type is DataListAgendaSectorType => (
            (DATA_LIST_AGENDA_SECTOR_TYPES as readonly string[]).includes(type as string)
        ))
        : []
);

export const createDataListAttrs = (source: DataListSource): PrintoutDataListAttrs => ({
    columns: createDataListColumns(source),
    emptyText: getDataListSourceDefinition(source).emptyText,
    hideWhenEmpty: source === "guestDietaryRequirements",
    sectorTypes: [],
    source,
    title: getDataListSourceDefinition(source).defaultTitle ?? "",
});

export const DEFAULT_DATA_LIST_ATTRS: PrintoutDataListAttrs = createDataListAttrs("folioItems");

export const getDataListAttrs = (attrs?: Partial<PrintoutDataListAttrs> | null): PrintoutDataListAttrs => {
    const source = parseEnumAttribute(attrs?.source, DATA_LIST_SOURCES, "folioItems");
    const columns = Array.isArray(attrs?.columns) && attrs!.columns.length
        ? attrs!.columns.map(column => normalizeColumn(source, column))
        : createDataListColumns(source);

    return {
        columns,
        emptyText: parseTextAttribute(attrs?.emptyText, getDataListSourceDefinition(source).emptyText),
        hideWhenEmpty: attrs?.hideWhenEmpty === true,
        sectorTypes: normalizeSectorTypes(attrs?.sectorTypes),
        source,
        title: parseTextAttribute(attrs?.title, ""),
    };
};
